import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  // Compact mode renders an inline fallback (for a boundary scoped to one
  // tab/panel) instead of the full-screen one, with a "Reload tab" button
  // that resets the boundary in place — remounting just the crashed
  // subtree — rather than reloading the whole app. Pair with a `key` prop
  // on the boundary that changes with the active tab so switching tabs
  // also clears a stale error automatically.
  compact?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.compact) {
      return (
        <div className="p-4 border border-rose-900/50 bg-rose-950/20 rounded-lg text-center">
          <p className="text-sm font-semibold text-rose-300 mb-1">This tab hit an error.</p>
          {this.state.error && (
            <p className="text-xs text-slate-400 mb-3 break-words">{this.state.error.message}</p>
          )}
          <button
            onClick={this.handleReset}
            className="px-4 py-1.5 text-xs font-semibold bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors"
          >
            Reload tab
          </button>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <h1 className="text-2xl font-bold text-white mb-2">
            Something went wrong
          </h1>
          <p className="text-slate-400 mb-6">
            An unexpected error occurred. Reloading usually fixes it.
          </p>
          {this.state.error && (
            <pre className="text-xs text-red-400 bg-slate-900 rounded-lg p-3 mb-6 text-left overflow-auto max-h-32">
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-sky-600 text-white font-semibold rounded-xl hover:bg-sky-500 transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
